import Grid from '@mui/material/Grid2';
import { Typography, Button, Card, CardContent, CardMedia, Box, Snackbar, Modal, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useAuth } from '../AuthContext'; // Importer le contexte
import useAuthenticatedFetch from '../hooks/useAuthenticatedFetch';
import { useTranslation } from "react-i18next";
import { ViewModule, ViewList, ExpandMore } from '@mui/icons-material'; // Importer les icônes
import { useMediaQuery } from '@mui/material';
import { useTheme } from "@mui/material/styles";

function TemplateSelection({ onIsDirtyChange }) {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedSecondTemplate, setSelectedSecondTemplate] = useState(null);
  const [hasSecondTemplate, setHasSecondTemplate] = useState(false);
  const { user } = useAuth(); // Accéder à l'état global
  const [successMessage, setSuccessMessage] = useState(''); // Pour le message de confirmation
  const [errorMessage, setErrorMessage] = useState(''); // Pour le message de confirmation
  const fetchWithAuth = useAuthenticatedFetch();
  const { t } = useTranslation(); // Pour accéder à i18n
  const [open, setOpen] = useState(false); // Pour gérer le modal de loupe
  const [zoomImage, setZoomImage] = useState(''); // Pour l'image à afficher dans la loupe
  const [viewMode, setViewMode] = useState('grid'); // État pour le mode d'affichage
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md")); // Vérifie si l'écran est petit
  const [isDirty, setIsDirty] = useState(false);

  const handleSwitchView = () => {
    setViewMode((prevMode) => (prevMode === 'grid' ? 'list' : 'grid'));
  };

  useEffect(() => {
    if (onIsDirtyChange) {
      onIsDirtyChange(isDirty); // Notifier le parent chaque fois que isDirty change
    }
  }, [isDirty, onIsDirtyChange]);
  
  useEffect(() => {
    if (isSmallScreen) {
      setViewMode('grid'); // Forcer le mode grid si l'écran est petit
    }
  }, [isSmallScreen]);

  useEffect(() => {
    // Charger les templates et la configuration de la communauté
    Promise.all([
      fetch(`${process.env.REACT_APP_BASE_URL}/template/getTemplates?communityId=${user.communityId}`),
      fetch(`${process.env.REACT_APP_BASE_URL}/community/getCommunityById/${user.communityId}`)
    ])
    .then(([templatesRes, communityRes]) => Promise.all([templatesRes.json(), communityRes.json()]))
    .then(([templatesData, communityData]) => {
      setTemplates(templatesData);
      setHasSecondTemplate(communityData.OPT_SecondTemplate);
      
      const initialTemplate = templatesData.find(t => t.isSelected);
      if (initialTemplate) {
        setSelectedTemplate(initialTemplate.templateId);
      }
      
      if (communityData.selectedTemplate2Id) {
        setSelectedSecondTemplate(communityData.selectedTemplate2Id);
      }
    })
    .catch(err => console.error('Erreur lors du chargement:', err));
  }, [user.communityId]);

  const handleSaveTemplate = async () => {
    try {
      await fetchWithAuth(`${process.env.REACT_APP_BASE_URL}/template/saveTemplate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          communityId: user.communityId, 
          selectedTemplate,
          selectedTemplate2: hasSecondTemplate ? selectedSecondTemplate : null
        }),
      });

      setSuccessMessage('Templates sauvegardés avec succès !');
      setIsDirty(false);
    } catch (error) {
      setErrorMessage('Erreur lors de la sauvegarde des templates');
    }
  };

  const handleClose = () => setOpen(false);
  const handleImageClick = (imagePath) => {
    setZoomImage(imagePath); // Définir l'image à afficher dans la loupe
    setOpen(true); // Ouvrir le modal
  };

  const handleResetTemplate = () => {
    setSelectedTemplate(null); // Réinitialise le template sélectionné
  };

  // Ajouter des gestionnaires de clic séparés pour les templates
  const handleTemplateClick = (templateId) => {
    if (templateId !== selectedTemplate) {
      setSelectedTemplate(templateId);
      setIsDirty(true);
    }
  };

  const handleSecondTemplateClick = (templateId) => {
    if (templateId !== selectedSecondTemplate) {
      setSelectedSecondTemplate(templateId);
      setIsDirty(true);
    }
  };

  return (
    <Box sx={{ mt: 1, width: '100%' }}>
      {/* Titre aligné à gauche */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1, width: '100%' }}>
        <Typography variant="h4" gutterBottom>
          {t("template.title")}
        </Typography>

        {/* Bouton pour changer le mode d'affichage */}
        {!isSmallScreen && (<Button
          variant="outlined"
          onClick={handleSwitchView}
          startIcon={viewMode === 'grid' ? <ViewModule /> : <ViewList />}
        >
          {viewMode === 'grid' ? t("template.viewGrid") : t("template.viewList")}
        </Button>)}
      </Box>

      {hasSecondTemplate ? (
        // Affichage avec accordéons si hasSecondTemplate est true
        <>
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="h6">{t("template.firstTemplate")}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid
                container
                spacing={viewMode === 'grid' ? 2 : 0}
                direction={viewMode === 'grid' ? 'row' : 'column'}
                sx={{ mt: 3 }}
                justifyContent={viewMode === 'grid' ? 'center' : 'flex-start'}
              >       
                {templates.map((template) => (
                  <Grid
                    item
                    xs={12}
                    sm={!isSmallScreen && viewMode === 'list' ? 12 : 6}
                    md={!isSmallScreen && viewMode === 'list' ? 12 : 6}
                    key={template.templateId}
                  >
                    <Card
                      sx={{
                        display: !isSmallScreen && viewMode === 'list' ? 'flex' : 'block',
                        alignItems: !isSmallScreen && viewMode === 'list' ? 'center' : 'inherit',
                        maxWidth: viewMode === 'grid' ? 345 : '100%',
                        cursor: 'pointer',
                        border: selectedTemplate === template.templateId ? '3px solid blue' : 'none',
                        boxShadow: 3,
                        mb: !isSmallScreen && viewMode === 'list' ?  2 : 0,
                      }}
                      onClick={() => handleTemplateClick(template.templateId)}
                    >
                      <CardMedia
                        component="img"
                        alt={template.templateName}
                        height={!isSmallScreen && viewMode === 'list' ? '100' : '140'}
                        image={template.templatePath}
                        onClick={(e) => {
                          e.stopPropagation(); // Empêche le clic de buller vers la Card
                          handleImageClick(template.templatePath);
                        }}
                        sx={{
                          width: !isSmallScreen && viewMode === 'list' ? '30%' : '100%',
                          height: !isSmallScreen && viewMode === 'list' ? '150px' : '150px',
                          objectFit: 'cover',
                          '&:hover': { transform: 'scale(1.10)' },
                          transition: 'transform 0.3s ease',
                        }}
                      />
                      <CardContent
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        padding: !isSmallScreen && viewMode === 'list' ? 1 : 'inherit',
                        width: !isSmallScreen && viewMode === 'list' ? '100%' : 'inherit',
                      }}>
                        <Typography variant="h6" component="div" align="left">
                          {template.templateName}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="h6">{t("template.secondTemplate")}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid
                container
                spacing={viewMode === 'grid' ? 2 : 0}
                direction={viewMode === 'grid' ? 'row' : 'column'}
                sx={{ mt: 3 }}
                justifyContent={viewMode === 'grid' ? 'center' : 'flex-start'}
              >       
                {templates.map((template) => (
                  <Grid
                    item
                    xs={12}
                    sm={!isSmallScreen && viewMode === 'list' ? 12 : 6}
                    md={!isSmallScreen && viewMode === 'list' ? 12 : 6}
                    key={template.templateId}
                  >
                    <Card
                      sx={{
                        display: !isSmallScreen && viewMode === 'list' ? 'flex' : 'block',
                        alignItems: !isSmallScreen && viewMode === 'list' ? 'center' : 'inherit',
                        maxWidth: viewMode === 'grid' ? 345 : '100%',
                        cursor: 'pointer',
                        border: selectedSecondTemplate === template.templateId ? '3px solid blue' : 'none',
                        boxShadow: 3,
                        mb: !isSmallScreen && viewMode === 'list' ?  2 : 0,
                      }}
                      onClick={() => handleSecondTemplateClick(template.templateId)}
                    >
                      <CardMedia
                        component="img"
                        alt={template.templateName}
                        height={!isSmallScreen && viewMode === 'list' ? '100' : '140'}
                        image={template.templatePath}
                        onClick={(e) => {
                          e.stopPropagation(); // Empêche le clic de buller vers la Card
                          handleImageClick(template.templatePath);
                        }}
                        sx={{
                          width: !isSmallScreen && viewMode === 'list' ? '30%' : '100%',
                          height: !isSmallScreen && viewMode === 'list' ? '150px' : '150px',
                          objectFit: 'cover',
                          '&:hover': { transform: 'scale(1.10)' },
                          transition: 'transform 0.3s ease',
                        }}
                      />
                      <CardContent
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        padding: !isSmallScreen && viewMode === 'list' ? 1 : 'inherit',
                        width: !isSmallScreen && viewMode === 'list' ? '100%' : 'inherit',
                      }}>
                        <Typography variant="h6" component="div" align="left">
                          {template.templateName}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </AccordionDetails>
          </Accordion>
        </>
      ) : (
        // Affichage simple sans accordéon si hasSecondTemplate est false
        <Grid
          container
          spacing={viewMode === 'grid' ? 2 : 0}
          direction={viewMode === 'grid' ? 'row' : 'column'}
          sx={{ mt: 3 }}
          justifyContent={viewMode === 'grid' ? 'center' : 'flex-start'}
        >       
          {templates.map((template) => (
            <Grid
              item
              xs={12}
              sm={!isSmallScreen && viewMode === 'list' ? 12 : 6}
              md={!isSmallScreen && viewMode === 'list' ? 12 : 6}
              key={template.templateId}
            >
              <Card
                sx={{
                  display: !isSmallScreen && viewMode === 'list' ? 'flex' : 'block',
                  alignItems: !isSmallScreen && viewMode === 'list' ? 'center' : 'inherit',
                  maxWidth: viewMode === 'grid' ? 345 : '100%',
                  cursor: 'pointer',
                  border: selectedTemplate === template.templateId ? '3px solid blue' : 'none',
                  boxShadow: 3,
                  mb: !isSmallScreen && viewMode === 'list' ?  2 : 0,
                }}
                onClick={() => handleTemplateClick(template.templateId)}
              >
                <CardMedia
                  component="img"
                  alt={template.templateName}
                  height={!isSmallScreen && viewMode === 'list' ? '100' : '140'}
                  image={template.templatePath}
                  onClick={(e) => {
                    e.stopPropagation(); // Empêche le clic de buller vers la Card
                    handleImageClick(template.templatePath);
                  }}
                  sx={{
                    width: !isSmallScreen && viewMode === 'list' ? '30%' : '100%',
                    height: !isSmallScreen && viewMode === 'list' ? '150px' : '150px',
                    objectFit: 'cover',
                    '&:hover': { transform: 'scale(1.10)' },
                    transition: 'transform 0.3s ease',
                  }}
                />
                <CardContent
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  padding: !isSmallScreen && viewMode === 'list' ? 1 : 'inherit',
                  width: !isSmallScreen && viewMode === 'list' ? '100%' : 'inherit',
                }}>
                  <Typography variant="h6" component="div" align="left">
                    {template.templateName}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Modal pour afficher la loupe */}
      <Modal open={open} onClose={handleClose} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box
          component="img"
          src={zoomImage}
          alt="Zoomed template"
          sx={{ maxWidth: '90%', maxHeight: '90%', border: '5px solid white', boxShadow: 24 }}
        />
      </Modal>

      {/* Boutons alignés */}
      <Grid container spacing={2} justifyContent="flex-start" sx={{ mt: 3 }}>
        <Grid item>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveTemplate}
            disabled={!selectedTemplate}
            sx={{ padding: '8px 16px' }}
          >
            Sauvegarder
          </Button>
        </Grid>
        <Grid item>
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleResetTemplate}
            sx={{ padding: '8px 16px' }}
          >
            Reset
          </Button>
        </Grid>
      </Grid>

      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage('')}
        message={successMessage}
      />
      <Snackbar
        open={!!errorMessage}
        autoHideDuration={3000}
        onClose={() => setErrorMessage('')}
        message={errorMessage}
        severity="error"
      />
    </Box>
  );
}

export default TemplateSelection;
